/**
 * SureBright quote adapter for the PF Scroll pilot warranty selector.
 *
 * The selector renders and binds coverage from SureBright's quote API directly, so it does not
 * depend on the DOM or storage of any SureBright storefront SDK (legacy or v2).
 */
(function (root) {
  "use strict";

  const API_BASE = "https://api.surebright.com";
  const TERMS_BASE = "https://customer.surebright.com/t-c";
  const FETCHED_STATUS = "POLICY_FETCHED_SUCCESSFULLY";
  const CACHE_STORAGE_KEY = "pf_sb_quotes_v1";
  const VISIT_STORAGE_KEY = "pf_sb_visit_id";
  const SUREBRIGHT_VISIT_STORAGE_KEY = "__sb_visit_id__";
  const OK_TTL_MS = 15 * 60 * 1000;
  const DISABLED_TTL_MS = 5 * 60 * 1000;
  // Phones on mobile data regularly need 3-8s for this 7KB response while the page loads, even
  // though the API answers in ~0.2s. Add to cart never waits on it, so give it room to arrive.
  const REQUEST_TIMEOUT_MS = 15000;

  const memoryCache = new Map();
  const inFlight = new Map();

  const text = (value) => {
    const normalized = String(value ?? "").trim();
    return /^(none|null|undefined)$/i.test(normalized) ? "" : normalized;
  };

  const flag = (value) => value === true || /^true$/i.test(String(value ?? "").trim());

  const priceCents = (value) => {
    const match = text(value)
      .replace(/,/g, "")
      .match(/^(\d+)(?:\.(\d{1,2}))?$/);
    if (!match) return null;
    const cents = Number(match[1]) * 100 + Number(String(match[2] || "").padEnd(2, "0"));
    return Number.isSafeInteger(cents) && cents > 0 ? cents : null;
  };

  // The protection block renders either panel style (Shoplift template test); events report which.
  const uiVersion = () => {
    const selector = root.document?.querySelector?.("[data-pf-scroll-warranty-selector]");
    if (!selector) return undefined;
    return selector.dataset.pfScrollWarrantyUi === "quick" ? "quick" : "current";
  };

  // The block's "Selection motion" setting, so events can split by whether the motion was on.
  const selectionMotion = () => {
    const selector = root.document?.querySelector?.("[data-pf-scroll-warranty-selector]");
    if (!selector) return undefined;
    return selector.dataset.pfScrollWarrantyMotion === "on" ? "on" : "off";
  };

  // Shoplift protection-first test: templates/product.protection-first.json moves the protection row
  // above the "Complete your setup" add-ons. Read the order from the page so events split by arm
  // whichever template served it.
  const protectionPosition = () => {
    const doc = root.document;
    const rows = Array.from(doc?.querySelectorAll?.(".pf-product-scroll__row") || []);
    const warrantyIndex = rows.findIndex((row) => row.matches("[data-pf-warranty-row]"));
    const addOnsIndex = rows.findIndex((row) =>
      row.querySelector("[data-pf-scroll-complementary-selector]")
    );
    if (warrantyIndex < 0 || addOnsIndex < 0) return undefined;
    return warrantyIndex < addOnsIndex ? "before_add_ons" : "after_add_ons";
  };

  const formatPrice = (cents) =>
    cents % 100 === 0 ? `$${cents / 100}` : `$${(cents / 100).toFixed(2)}`;

  const policyYears = (policy) =>
    text(policy?.coverageYears).match(/^([1-3])(?:\s*[- ]?\s*years?)?$/i)?.[1] || "";

  const formatTerm = (years) => `${years} ${String(years) === "1" ? "Year" : "Years"}`;

  const cacheKey = ({ productId, variantId, country, locale }) =>
    [productId, variantId, country || "", locale || ""].map(String).join("#");

  const storage = () => {
    try {
      return root.sessionStorage || null;
    } catch {
      return null;
    }
  };

  const readStoredCache = () => {
    try {
      const parsed = JSON.parse(storage()?.getItem(CACHE_STORAGE_KEY) || "{}");
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  };

  const writeStoredCache = (entries) => {
    try {
      storage()?.setItem(CACHE_STORAGE_KEY, JSON.stringify(entries));
    } catch {
      // Storage is a convenience only.
    }
  };

  const getCached = (key, now = Date.now()) => {
    const entry = memoryCache.get(key) || readStoredCache()[key];
    if (!entry || !entry.quote || !(Number(entry.expiresAt) > now)) return null;
    memoryCache.set(key, entry);
    return entry.quote;
  };

  const setCached = (key, quote, now = Date.now()) => {
    if (quote.status !== "ok" && quote.status !== "disabled") return;
    const entry = {
      expiresAt: now + (quote.status === "ok" ? OK_TTL_MS : DISABLED_TTL_MS),
      quote,
    };
    memoryCache.set(key, entry);
    const stored = readStoredCache();
    Object.keys(stored).forEach((storedKey) => {
      if (!(Number(stored[storedKey]?.expiresAt) > now)) delete stored[storedKey];
    });
    stored[key] = entry;
    writeStoredCache(stored);
  };

  const clearCache = () => {
    memoryCache.clear();
    inFlight.clear();
    try {
      storage()?.removeItem(CACHE_STORAGE_KEY);
    } catch {
      // Storage is a convenience only.
    }
  };

  const visitId = () => {
    const store = storage();
    try {
      const existing =
        text(store?.getItem(SUREBRIGHT_VISIT_STORAGE_KEY)) ||
        text(store?.getItem(VISIT_STORAGE_KEY));
      if (existing) return existing;
    } catch {
      // Fall through to a fresh visit id.
    }
    const created =
      typeof root.crypto?.randomUUID === "function"
        ? root.crypto.randomUUID()
        : `pf-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
    try {
      store?.setItem(VISIT_STORAGE_KEY, created);
    } catch {
      // Storage is a convenience only.
    }
    return created;
  };

  const emptyQuote = (status, ctx, extra = {}) => ({
    components: [],
    coverageType: "",
    learnMoreUrl: "",
    plans: [],
    productId: String(ctx?.productId || ""),
    productTitle: "",
    quoteId: "",
    raw: null,
    schemaType: "SIMPLE",
    status,
    statusMessage: "",
    variantId: String(ctx?.variantId || ""),
    ...extra,
  });

  /**
   * Turns a raw listQuotes response into the plan list the selector renders. Anything that cannot
   * be bound to the requested product and variant is dropped, and a quote with no usable plan is
   * reported as disabled so the warranty row stays hidden.
   */
  const normalizeQuoteResponse = (raw, ctx) => {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return emptyQuote("error", ctx);
    const statusMessage = text(raw.statusMessage);
    if (statusMessage !== FETCHED_STATUS || !Array.isArray(raw.PolicyData)) {
      return emptyQuote("disabled", ctx, { statusMessage });
    }

    const productId = String(ctx?.productId || "");
    const variantId = String(ctx?.variantId || "");
    const plansByYears = new Map();
    raw.PolicyData.forEach((policy) => {
      const years = policyYears(policy);
      const cents = priceCents(policy?.price);
      const policyVariantId = text(policy?.variantId);
      const quoteItemId = text(policy?.quoteItemId);
      const quoteId = text(policy?.quoteId) || text(raw.quoteId);
      const storeProductId = text(policy?.storeProductId);
      const storeProductVariantId = text(policy?.storeProductVariantId);
      if (
        !years ||
        !cents ||
        !/^\d+$/.test(policyVariantId) ||
        !quoteItemId ||
        !quoteId ||
        (storeProductId && storeProductId !== productId) ||
        (storeProductVariantId && storeProductVariantId !== variantId) ||
        plansByYears.has(years)
      )
        return;
      plansByYears.set(years, {
        deductible: Number(policy?.deductibleAmount) || 0,
        isDefault: flag(policy?.defaultSelection),
        policyVariantId,
        priceCents: cents,
        priceText: formatPrice(cents),
        priceVersionId: text(policy?.priceVersionId) || "001",
        quoteId,
        quoteItemId,
        ribbon: flag(policy?.ribbonEnabled),
        ribbonText: text(policy?.ribbonText),
        years,
      });
    });

    const plans = Array.from(plansByYears.values()).sort(
      (a, b) => Number(a.years) - Number(b.years)
    );
    if (!plans.length) return emptyQuote("disabled", ctx, { statusMessage });

    return emptyQuote("ok", ctx, {
      components: Array.isArray(raw.componentQuoteList) ? raw.componentQuoteList : [],
      coverageType: text(raw.coverageType),
      learnMoreUrl:
        text(raw.tncLink) ||
        (/^[a-f0-9]{32,}$/i.test(text(raw.policyId))
          ? `${TERMS_BASE}/${text(raw.policyId)}`
          : text(raw.sbCategoryPageURL)),
      plans,
      productTitle: text(raw.productTitle),
      quoteId: text(raw.quoteId),
      raw,
      schemaType: text(raw.productSchemaType).toUpperCase() || "SIMPLE",
      statusMessage,
    });
  };

  const buildQuoteUrl = ({ shop, productId, variantId, productHandle, country, locale }) => {
    const params = new URLSearchParams({
      country: country || "US",
      currencyConversionRate: "1.0",
      locale: locale || "en-US",
      visitId: visitId(),
      productHandle: productHandle || "",
      productId: String(productId),
      variantId: String(variantId),
    });
    return `${API_BASE}/quote/api/quote/listQuotes/${encodeURIComponent(shop)}?${params}`;
  };

  const requestQuote = async (url, signal) => {
    const controller = new AbortController();
    const abort = () => controller.abort();
    const timer = setTimeout(abort, REQUEST_TIMEOUT_MS);
    if (signal?.aborted) abort();
    signal?.addEventListener?.("abort", abort, { once: true });
    try {
      const response = await root.fetch(url, {
        credentials: "omit",
        headers: { Accept: "application/json" },
        method: "GET",
        signal: controller.signal,
      });
      if (!response.ok) {
        const error = new Error(`SureBright quote request failed with ${response.status}`);
        error.reason = `http_${response.status}`;
        throw error;
      }
      try {
        return await response.json();
      } catch (bodyError) {
        // The timeout aborts the body read too; report that as a timeout, not bad JSON.
        if (signal?.aborted) throw bodyError;
        if (controller.signal.aborted) {
          const error = new Error("SureBright quote request timed out");
          error.reason = "timeout";
          throw error;
        }
        const error = new Error("SureBright quote response was not JSON");
        error.reason = "parse";
        throw error;
      }
    } finally {
      clearTimeout(timer);
      signal?.removeEventListener?.("abort", abort);
    }
  };

  /**
   * Resolves the quote for one product variant. Rejects only when the caller aborts; every other
   * failure resolves to an "error" quote so the selector can fail closed.
   */
  const fetchPlans = (ctx) => {
    const { shop, productId, variantId, signal } = ctx || {};
    if (!text(shop) || !/^\d+$/.test(String(productId)) || !/^\d+$/.test(String(variantId))) {
      return Promise.resolve(emptyQuote("error", ctx, { failureReason: "context" }));
    }
    const key = cacheKey(ctx);
    const cached = getCached(key);
    if (cached) return Promise.resolve({ ...cached, cacheHit: true });
    // Share a request only while its owner still wants it. An aborted owner's promise is about to
    // reject, so a later caller that reused it would never get a quote.
    const shared = inFlight.get(key);
    if (shared && !shared.signal?.aborted) return shared.promise;

    const startedAt = Date.now();
    const url = buildQuoteUrl(ctx);
    const attempt = (retriesLeft) =>
      requestQuote(url, signal).catch((error) => {
        if (signal?.aborted) throw error;
        if (retriesLeft > 0 && !error.reason && error.name !== "AbortError") {
          return attempt(retriesLeft - 1);
        }
        throw error;
      });

    const entry = { promise: null, signal };
    entry.promise = attempt(1)
      .then((raw) => {
        const quote = normalizeQuoteResponse(raw, ctx);
        setCached(key, quote);
        return { ...quote, cacheHit: false, fetchMs: Date.now() - startedAt };
      })
      .catch((error) => {
        if (signal?.aborted) throw error;
        return emptyQuote("error", ctx, {
          failureReason: error.reason || (error.name === "AbortError" ? "timeout" : "network"),
          fetchMs: Date.now() - startedAt,
        });
      })
      .finally(() => {
        if (inFlight.get(key) === entry) inFlight.delete(key);
      });
    inFlight.set(key, entry);
    return entry.promise;
  };

  const lineItemError = (code, message) => {
    const error = new Error(message);
    error.code = code;
    return error;
  };

  /**
   * Builds the policy line items for one selected plan. SureBright identifies a policy line by
   * its properties, and its cart sync associates the line to the covered product by _variantId.
   */
  const buildWarrantyLineItems = (quote, plan, ctx) => {
    const productId = String(ctx?.productId || "");
    const variantId = String(ctx?.variantId || "");
    const mainQuantity = Number(ctx?.mainQuantity || 0);
    const addedAt = String(ctx?.addedAt || Date.now());
    const raw = quote?.raw;
    const selectedYear = String(plan?.years || "");
    if (
      quote?.status !== "ok" ||
      !raw ||
      !/^\d+$/.test(productId) ||
      !/^\d+$/.test(variantId) ||
      String(quote.productId) !== productId ||
      String(quote.variantId) !== variantId ||
      !/^[1-3]$/.test(selectedYear) ||
      !Number.isInteger(mainQuantity) ||
      mainQuantity < 1
    ) {
      throw lineItemError("unbound", "Coverage is not bound to the current product quote.");
    }

    const selectedPolicies = raw.PolicyData.filter(
      (policy) =>
        text(policy?.quoteItemId) === plan.quoteItemId && policyYears(policy) === selectedYear
    );
    if (selectedPolicies.length !== 1) {
      throw lineItemError("unbound", "Coverage does not match one policy in the current quote.");
    }
    const selectedPolicy = selectedPolicies[0];

    if (quote.schemaType !== "SIMPLE" && quote.schemaType !== "BUNDLE") {
      throw lineItemError("unsupported_schema", quote.schemaType);
    }

    const expectedTotalCents = priceCents(selectedPolicy.price);
    const productTitle = text(quote.productTitle) || text(ctx?.productTitle);
    const term = formatTerm(selectedYear);
    if (!expectedTotalCents || !productTitle) {
      throw lineItemError("unbound", "Coverage is missing a price or product title.");
    }

    const expectedWarrantyLines = [];
    if (quote.schemaType === "SIMPLE") {
      expectedWarrantyLines.push({
        componentQuantity: 1,
        coveredVariantId: variantId,
        expectedUnitPriceCents: expectedTotalCents,
        item: {
          id: plan.policyVariantId,
          properties: {
            Product: productTitle,
            Term: term,
            _sbPriceVersionId: plan.priceVersionId,
            _sbQuoteId: plan.quoteId,
            _sbQuoteItemId: plan.quoteItemId,
            _variantId: variantId,
            _sbAddedAt: addedAt,
          },
          quantity: mainQuantity,
        },
        quoteId: plan.quoteId,
        quoteItemId: plan.quoteItemId,
      });
    } else {
      const components = quote.components;
      const coveredVariantIds = new Set();
      const quoteItemIds = new Set();
      if (!components.length) {
        throw lineItemError("unbound", "Bundle coverage has no component quotes.");
      }
      components.forEach((component) => {
        const coveredVariantId = text(component?.componentVariantId);
        const componentQuantity = Number(component?.quantity || 1);
        const quoteId = text(component?.quoteId);
        const policies = (Array.isArray(component?.PolicyData) ? component.PolicyData : []).filter(
          (policy) => policyYears(policy) === selectedYear
        );
        const policy = policies[0];
        const warrantyVariantId = text(policy?.variantId);
        const quoteItemId = text(policy?.quoteItemId);
        const expectedUnitPriceCents = priceCents(policy?.price);
        if (
          !/^\d+$/.test(coveredVariantId) ||
          !Number.isInteger(componentQuantity) ||
          componentQuantity < 1 ||
          !quoteId ||
          policies.length !== 1 ||
          coveredVariantIds.has(coveredVariantId) ||
          !/^\d+$/.test(warrantyVariantId) ||
          !quoteItemId ||
          quoteItemIds.has(quoteItemId) ||
          !expectedUnitPriceCents
        ) {
          throw lineItemError("unbound", "Bundle coverage could not be bound to every component.");
        }

        coveredVariantIds.add(coveredVariantId);
        quoteItemIds.add(quoteItemId);
        const componentTitle = text(component.componentProductTitle);
        expectedWarrantyLines.push({
          componentQuantity,
          coveredVariantId,
          expectedUnitPriceCents,
          item: {
            id: warrantyVariantId,
            properties: {
              Product: componentTitle ? `${productTitle} - ${componentTitle}` : productTitle,
              Term: term,
              _variantId: coveredVariantId,
              _sbQuoteId: quoteId,
              _sbQuoteItemId: quoteItemId,
              _bundleParentVariantId: variantId,
              _bundleParentProductId: productId,
              _isBundleComponentPolicy: "true",
              _componentQuantity: String(componentQuantity),
              _bundleComponentCount: String(components.length),
              _sbPriceVersionId: text(policy.priceVersionId) || "001",
              _sbAddedAt: addedAt,
            },
            quantity: mainQuantity * componentQuantity,
          },
          quoteId,
          quoteItemId,
        });
      });

      const componentTotalCents = expectedWarrantyLines.reduce(
        (total, line) => total + line.expectedUnitPriceCents * line.componentQuantity,
        0
      );
      if (componentTotalCents !== expectedTotalCents) {
        throw lineItemError("unbound", "Bundle component prices do not add up to the plan price.");
      }
    }

    return {
      addedAt,
      expectedTotalCents,
      expectedWarrantyLines,
      schemaType: quote.schemaType,
      selectedPolicy,
    };
  };

  const sdkState = () => {
    if (root.__SUREBRIGHT__?.version) return "v2";
    const scripts = Array.from(root.document?.scripts || []);
    return scripts.some((script) => /surebright-extended-warranty/.test(script.src || ""))
      ? "v2"
      : "none";
  };

  const track = (event, values = {}) => {
    try {
      root.posthog?.capture?.(event, {
        experience: "pf_scroll_pilot",
        sdk_state: sdkState(),
        // Every plug_warranty_* event carries the panel style so the dashboard can split the
        // Shoplift arms: "current" (four-card row) and "quick" (slimmed panel).
        ui_version: uiVersion(),
        selection_motion: selectionMotion(),
        protection_position: protectionPosition(),
        ...values,
      });
    } catch {
      // Analytics must never affect the storefront.
    }
  };

  root.PfSureBrightQuotes = {
    buildQuoteUrl,
    buildWarrantyLineItems,
    cacheKey,
    clearCache,
    fetchPlans,
    formatTerm,
    getCached,
    normalizeQuoteResponse,
    track,
  };
})(typeof window !== "undefined" ? window : globalThis);
